import BankIcon from '@components/v2/BankIcon';
import CurrencyFormatter from '@components/CurrencyFormatter';
import {
  Box,
  Flex,
  Text,
  Pagination,
} from '@huspy/briks-web';
import { BankOffersEntity, ProposalEntity } from '@modules/clients/entities/client/api/dto/clientProposal.dto';
import { BANK_SELECTION_RULES } from '@modules/opportunities/rules';
import { INTEREST_RATE } from '@modules/opportunities/entities/bankApplication/const';
import { convertToDDMMYYYYFormat, formatPercentage } from '@shared/utils';
import { useNavigate } from '@tanstack/react-router';
import React, {
  useEffect, useMemo, useState
} from 'react';
import { getCurrentShownRange } from '@components/Table';
import Table from '@components/v2/Table';
import { useTranslation } from 'react-i18next';
import useFeatureFlags from '@modules/core/hooks/useFeatureFlags';
import { AMPLITUDE_FEATURE_FLAGS } from '@modules/core/api/types';
import { ProposalsDTO } from '@modules/proposals/api/dto/proposals.dto';
import { useDisclosure } from '@mantine/hooks';
import { PROPOSAL_CREATION_RULES } from '@modules/proposals/presentation/v2/pages/ProposalCreation/rules';
import { ProposalsListRoute } from '@modules/proposals/presentation/routes';
import clsx from 'clsx';
import ProposalItemsMenu from '@modules/proposals/presentation/v2/pages/List/features/ProposalsTable/ae/features/ItemMenu';
import { ProposalPreviewSlido } from '@modules/proposals/presentation/v2/pages/List/features/ProposalsTable/ae/features/ProposalPreviewSlido';
import { ProposalDomain } from '@modules/proposals/domain/proposal';
import { proposalsStyles } from '@modules/proposals/presentation/v2/pages/List/features/ProposalsTable/ae/styles';
import Tooltip from '@components/Tooltip';

const {
  bankContainer,
  bankIconPlaceholder,
  noBankContainer,
  noWrapCell,
  firstHeaderCell,
} = proposalsStyles;

const NoBankSelected = ({ bankNumber }: { bankNumber: number }) => (
  <Flex justify='center' align='center' className={ noBankContainer }>
    <Text size='md' color='text-tertiary'>
      Bank
      {' '}
      {bankNumber}
    </Text>
  </Flex>
);

const PAGE_SIZE = 7;

const BankProduct = ({ ...bankOffer }: BankOffersEntity) => {
  const { t } = useTranslation();

  return (
    <Flex align='center' className={ bankContainer } gap={ 3 } data-testid={ `bank-offer-${bankOffer.external_id}` }>
      <Tooltip label={ bankOffer.bank_name }>
        <Flex className={ bankIconPlaceholder }>
          <BankIcon
            icon={ bankOffer.bank_icon }
            hasShadow
            style={ { height: '100%', width: '100%' } }
          />
        </Flex>
      </Tooltip>
      <Flex direction='column' gap={ 0 }>
        <Box>
          {
            bankOffer.interest_rate_type === INTEREST_RATE.variable
              ? formatPercentage(bankOffer.variable_rate_addition)
              : formatPercentage(bankOffer.fixed_rate)
          }
          %
        </Box>

        <Box>
          <Text weight='regular' color='text-secondary'>
            { bankOffer.interest_rate_type === INTEREST_RATE.variable
              ? t('proposals.table.variableRate')
              : t('proposals.table.fixedRate') }
          </Text>
        </Box>
      </Flex>
    </Flex>
  );
};

type Props = {
  data?: ProposalsDTO;
  isLoading?: boolean;
  error?: any;
};

export const ProposalsTableAE = ({ data, isLoading, error }: Props) => {
  const navigate = useNavigate({ from: ProposalsListRoute.fullPath });
  const [selectedProposal, setSelectedProposal] = useState<ProposalEntity | null>(null);
  const [isOpen, { open, close }] = useDisclosure(false);

  const { t } = useTranslation();

  const {
    page, sort, order, proposal_id,
  } = ProposalsListRoute.useSearch();

  const proposalPreviewClose = () => {
    close();
    navigate({
      search: (prev) => ({
        ...prev,
        proposal_id: '',
      }),
    });
  };

  const handleSortableHeadClick = (sortBy: string) => {
    if (sortBy === sort) {
      navigate({
        search: ({ order: _order, sort: _sort, ...prev }) => ({
          ...(_order === 'DESC' ? { ...prev } : {
            ...prev,
            sort: sortBy,
            order: order === 'ASC' ? 'DESC' : 'ASC',
          }),
        }),
      });
    } else {
      navigate({
        search: (prev) => ({
          ...prev,
          sort: sortBy,
          order: 'ASC',
        }),
      });
    }
  };

  const currentlyShowingText = getCurrentShownRange(data?.page, PAGE_SIZE, data?.total);

  const handlePageSelect = (value: number) => {
    navigate({
      search: (prev) => ({
        ...prev,
        page: value,
      }),
    });
  };

  const { featureFlags } = useFeatureFlags();

  const onProposalSelect = (proposal: ProposalEntity) => {
    if (
      featureFlags?.[AMPLITUDE_FEATURE_FLAGS.CLIENT_HUB_NEW_PCS_CREDITS_FLOW]
      && ProposalDomain.isGenerated(proposal)
    ) {
      setSelectedProposal(proposal);
      open();
      navigate({
        search: (prev) => ({
          ...prev,
          proposal_id: proposal.external_id,
        }),
      });
      return;
    }

    navigate({
      to: PROPOSAL_CREATION_RULES.getCreateProposalLatestRoute(proposal),
      params: { id: proposal.external_id },
    });
  };

  useEffect(() => {
    if (proposal_id && data?.proposals) {
      const proposalToSelect = data?.proposals.find((proposal) => proposal.external_id === proposal_id);

      if (proposalToSelect) {
        onProposalSelect(proposalToSelect);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const rows = useMemo(
    // eslint-disable-next-line sonarjs/cognitive-complexity
    () =>
      data?.proposals?.map((element) => {
        const emptyBankList = new Array(
          BANK_SELECTION_RULES.UAE.maxAllowedSelection
            - element.bank_offers!.length || 0
        ).fill(0);

        return (
          <Table.Row
            key={ element.external_id }
            data-test={ `proposals-row-${element.external_id}` }
            data-testid={ `proposals-row-${element.external_id}` }
            style={ { cursor: 'pointer' } }
            onClick={ () => { onProposalSelect(element); } }
          >
            <Table.Cell>
              <Flex className={ noWrapCell }>
                {element.first_name}
                {' '}
                {element.last_name}
              </Flex>
            </Table.Cell>
            <Table.Cell>
              <Flex className={ noWrapCell }>
                {convertToDDMMYYYYFormat(element.updated_at)}
              </Flex>
            </Table.Cell>
            <Table.Cell className={ noWrapCell }>
              <CurrencyFormatter value={ element.property_value } currency='' />
            </Table.Cell>
            <Table.Cell className={ noWrapCell }>
              <CurrencyFormatter value={ element.loan_amount } currency='' />
            </Table.Cell>
            <Table.Cell py='3'>
              <Flex direction='row'>
                <Flex flex={ 1 } direction='row' gap={ 2 }>
                  {element.bank_offers ? (
                    <>
                      {element.bank_offers.map((bankOffer, i) => (
                        <BankProduct
                          // eslint-disable-next-line react/no-array-index-key
                          key={ `${bankOffer.external_id}-${i}` }
                          { ...bankOffer }
                        />
                      ))}
                      {emptyBankList.map((item, i) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <React.Fragment key={ `${item}-${i}-no-bank` }>
                          {element.bank_offers ? (
                            <NoBankSelected
                              // eslint-disable-next-line react/no-array-index-key
                              key={ `${item}-${i}-no-bank` }
                              bankNumber={ element.bank_offers.length + i + 1 }
                            />
                          ) : null}
                        </React.Fragment>
                      ))}
                    </>
                  ) : null}
                </Flex>

                <ProposalItemsMenu
                  proposal={ element }
                />
              </Flex>
            </Table.Cell>
          </Table.Row>
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data?.page, data?.proposals]
  );
  return (
    <Box width='100%'>
      <Table.Root>
        <Table.Head>
          <Table.Row>
            <Table.SortableHeader
              className={ clsx(noWrapCell, firstHeaderCell) }
              isSortedByThis={ sort === 'client_name' }
              order={ order }
              onClick={ () => handleSortableHeadClick('client_name') }
            >
              {t('proposals.table.clientName')}
            </Table.SortableHeader>

            <Table.Header>
              {t('proposals.table.lastModified')}
            </Table.Header>

            <Table.SortableHeader
              className={ noWrapCell }
              isSortedByThis={ sort === 'property_value' }
              order={ order }
              onClick={ () => handleSortableHeadClick('property_value') }
            >
              {t('proposals.table.propertyValue')}
            </Table.SortableHeader>

            <Table.SortableHeader
              className={ noWrapCell }
              isSortedByThis={ sort === 'loan_amount' }
              order={ order }
              onClick={ () => handleSortableHeadClick('loan_amount') }
            >
              {t('proposals.table.loanAmount')}
            </Table.SortableHeader>

            <Table.Header width='45%'>
              {t('proposals.table.bankProducts')}
            </Table.Header>
          </Table.Row>
        </Table.Head>
        <Table.Body>{ isLoading && !error ? <Table.Loading rows={ PAGE_SIZE - 2 } cells={ 5 } /> : rows }</Table.Body>
      </Table.Root>

      <Flex pt='spacing-5' justify='flex-end' align='center' gap='5'>
        <Text size='md' color='text-tertiary'>
          Showing
          {' '}
          {currentlyShowingText}
          {' '}
          of
          {' '}
          {data?.total ?? 0}
          {' '}
          items
        </Text>
        <Pagination
          count={ data?.total ?? 0 }
          pageSize={ PAGE_SIZE }
          siblingCount={ 1 }
          defaultPage={ page }
          page={ data?.page }
          onPageChange={ (change: { page: number, pageSize: number }) => { handlePageSelect(change.page); } }
        />
      </Flex>

      { selectedProposal
        && (
          <ProposalPreviewSlido
            proposal={ selectedProposal }
            isOpen={ isOpen }
            open={ open }
            close={ proposalPreviewClose }
          />
        )}
    </Box>
  );
};
